/**
 * Typed search params for the `/inventory` route. The category filter and archived toggle live
 * in the URL so they are deep-linkable AND compile-checked (the invoices `searchParams` pattern).
 * TanStack Router validates the raw URL params through `validateInventorySearch` on every
 * navigation.
 *
 * `?category=<id>` narrows the list to one category (legacy parity — `InventoryListPage`
 * `selectedCategoryId`). `?archived=1` flips the list to archived items. The coarse endpoint owns
 * the filtering; the SPA only carries the flags.
 */

export interface InventorySearch {
  category?: number;
  archived?: boolean;
}

/** Coerce a raw URL value into a boolean (`1`/`true` → true). */
function asBool(value: unknown): boolean {
  return value === true || value === '1' || value === 'true';
}

/** Coerce a raw URL value into a positive integer id, or undefined. */
function asId(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

/**
 * TanStack Router `validateSearch` for the inventory list route. Only keeps each filter when
 * meaningful so the canonical URL stays clean (`/inventory` vs `/inventory?category=3`).
 */
export function validateInventorySearch(raw: Record<string, unknown>): InventorySearch {
  const search: InventorySearch = {};
  const category = asId(raw.category);
  if (category !== undefined) search.category = category;
  if (asBool(raw.archived)) search.archived = true;
  return search;
}
